import {
    Line, Matrix, namespace, Point,
} from '../../../../../FrontendBundle/Resources/js/ofcs';
import AbstractItem from './abstractItem';
import ResizeConnectorHandle from '../svg/handle/resizeConnectorHandle';

namespace('Ofcs.Editor.Item');

/**
 * @class ConnectorItem
 * @extends AbstractItem
 */
export default window.Ofcs.Editor.Item.ConnectorItem = AbstractItem.extend({

    type: 'connector',

    INDENT: 35, // Distance from Edge to center of drill
    DRILL_SIZE: 35, // Diameter of the drill
    ROUTER_SIZE: 8, // Width of the milled shaft
    SIDE_MARGIN: 100, // Margin on sides of each connector
    TOP_MARGIN: 60, // Margin on connector head, pointing to table center

    /**
     * Defines that an item can be moved
     * @property {boolean} supportsMove
     */
    supportsMove: false,

    /**
     * Defines that an item can be selected
     * amongst other items
     * @property {boolean} supportsMultiselection
     */
    supportsMultiselection: false,

    supportsWidth: false, // set to true for resize handles to work
    supportsHeight: false,
    supportsRotation: false,
    supportsPositioning: false,
    supportsDepth: false,
    supportsCollisions: true,
    supportsBidirectionalCollisions: true,
    supportsGuides: false,
    supportsCopying: false,

    defaults: {
        /**
         * @property {string} Type
         */
        type: 'Connector',

        /**
         * Current connecting line
         * @property {Line} attributes.line
         */
        line: new Line(new Point(0, 0), new Point(0, 0)),

        /**
         * Current line's offset w.r.t. segment's
         * start point
         * No default is set as this would override
         * custom setter function during instantiation
         * @property {number} attributes.lineOffset
         */

        /**
         * Drill point offset positions with respect
         * to line's center position
         * @property {Array.<number>} attributes.offsets
         */
        offsets: [],

        /**
         * Defines if item is billable
         * @property {boolean} attributes.billable
         */
        billable: true,
    },

    /**
     * Returns respective item property
     *
     * @param {string} key - property name
     * @return {*}
     */
    get(key) {
        if (key === 'position') {
            return this.get('line').getCenter();
        }
        if (key === 'width') {
            return this.get('line').getLength();
        }
        return AbstractItem.prototype.get.call(this, key);
    },

    /**
     * Sets respective item property
     *
     * @param {string} key - property name
     * @param {*} value - property value
     * @param {Object} optionsParam
     */
    set(key, value, optionsParam) {
        const { attrs, options } = this._normalizeSetParameters(key, value, optionsParam);

        if (!attrs.width && !attrs.position && attrs.line) {
            // If the line is updated, we automatially update widht and position,
            // because all mechanics in the editor watch for changes on these both
            // porperties
            attrs.width = attrs.line.getLength();
            attrs.position = attrs.line.getCenter();
            this.setOffset(attrs.line);
        }

        AbstractItem.prototype.set.call(this, attrs, options);
    },

    /**
     * sets attribute.lineOffset
     * @param {Line} line
     */
    setOffset(line) {
        const segment = this.getTargetSegment(line);
        this.set('lineOffset', segment.startPoint.getDistanceTo(line.startPoint));
    },

    /**
     * validates target line & offset
     * will try to maintain line's length over
     * offset.
     * Mutates target line
     *
     * @param {Line} line - target line
     * @param {number} offset - target offset, optional
     */
    validateLine(line, offset = this.get('lineOffset')) {
        const segment = this.getTargetSegment(line);
        const segmentLength = segment.getLength();
        const lineLength = line.getLength();
        const MIN_LENGTH = 2 * this.SIDE_MARGIN;

        /* eslint-disable no-param-reassign */

        if (lineLength > segmentLength) {
            // limit maximum line length
            line.startPoint = segment.startPoint.clone();
            line.endPoint = segment.endPoint.clone();
        } else if (lineLength + offset > segmentLength) {
            // maintain line length first and offset second
            const matrix = new Matrix();

            matrix.rotate(segment.getAngle(), segment.startPoint.x, segment.startPoint.y);
            matrix.translate(segmentLength - lineLength, 0);
            line.startPoint = segment.startPoint.clone().transform(matrix);
            line.endPoint = segment.endPoint.clone();
        } else if (lineLength < MIN_LENGTH) {
            // limit minimum line length
            const startMatrix = new Matrix();

            startMatrix.rotate(segment.getAngle(), segment.startPoint.x, segment.startPoint.y);
            const endMatrix = startMatrix.clone();
            startMatrix.translate(offset, 0);
            endMatrix.translate(offset + MIN_LENGTH, 0);

            line.startPoint = segment.startPoint.clone().transform(startMatrix);
            line.endPoint = segment.startPoint.clone().transform(endMatrix);
        }

        /* eslint-enable no-param-reassign */
    },

    /**
     * @constructor
     */
    initialize() {
        AbstractItem.prototype.initialize.call(this);

        // Initialize paths
        this.set('ext', this.calculateConnectorSegments());
        this.set('orig', this.get('ext'));
        this.set('safety', this.calculateMarginSegments());

        this.on('change', this.onItemChange, this);
    },

    /**
     * Parses response data into attributes
     *
     * @param {Object} response Response hash
     * @return {Object} Attribute hash
     */
    parse(response) {
        const data = AbstractItem.prototype.parse.call(this, response);

        if (data.line) {
            const startPoint = new Point(data.line.startPoint.x, data.line.startPoint.y);
            const endPoint = new Point(data.line.endPoint.x, data.line.endPoint.y);

            data.line = new Line(startPoint, endPoint);
        }

        return data;
    },

    /**
     * Calculates relative connector offsets with respect
     * to line center.
     *
     * @return {Array.<number>}
     */
    getConnectors() {
        const connectors = [];
        const length = this.get('line').getLength();
        let offset = (length / -2) + this.SIDE_MARGIN; // Start at outer edge
        const numberOfConnectors = this.getNumberOfConnectors(length);
        const gaps = numberOfConnectors - 1;
        const evenNumConnectors = numberOfConnectors - (numberOfConnectors % 2);
        const connectorOffset = (length - (2 * this.SIDE_MARGIN)) / gaps;

        if (numberOfConnectors === 0) {
            return [];
        }

        if (numberOfConnectors === 1) {
            return [0];
        }

        // If uneven amount of connectors, place a connector in the center
        if (evenNumConnectors < numberOfConnectors) {
            connectors.push(0);
        }

        // Iterate over half amount of connectors an place a connector on left and right side of center
        for (let i = 0; i < evenNumConnectors / 2; i += 1) {
            connectors.push(offset);
            connectors.push(-offset);
            offset += connectorOffset;
        }

        return connectors;
    },

    /**
     * @param {number} length
     * @return {number}
     */
    getNumberOfConnectors(length) {
        const breakpoints = [
            2001, // 3 connectors
            300, // 2 connectors
            300, // skip this via duplication
            0, // 0 connectors
        ];

        const breakpoint = breakpoints.findIndex(point => point <= length);

        return breakpoints.length - breakpoint - 1;
    },

    /**
     * Returns an appropriate translation matrix for each connector
     *
     * @return {string[]}
     */
    getConnectorMatrices() {
        const line = this.get('line');
        const connectors = this.getConnectors();
        const matrix = new Matrix();

        this.set('offsets', connectors);
        matrix.rotate(line.getAngle());

        return connectors.map(pos => matrix.clone().translate(pos, 0));
    },

    /**
     * Creates and links all connector paths
     * @return {Array}
     */
    calculateConnectorSegments() {
        const self = this;
        return this.getConnectorMatrices()
            .map(matrix => self.createConnectorPath(matrix))
            .reduce((prev, current) => prev.concat(current), []);
    },

    /**
     * Creates and links all margin paths
     * @return {Array}
     */
    calculateMarginSegments() {
        const self = this;
        return this.getConnectorMatrices()
            .map(matrix => self.createMarginPath(matrix))
            .reduce((prev, current) => prev.concat(current), []);
    },

    /**
     * Calculates and stores connector path
     * @param {Matrix} matrixParam
     * @return {Array}
     */
    createConnectorPath(matrixParam) {
        const radius = this.DRILL_SIZE / 2;
        const intersectionIndent = this.INDENT - Math.sqrt((radius ** 2) - ((this.ROUTER_SIZE / 2) ** 2));
        const radians = Math.acos(1 - ((this.ROUTER_SIZE * this.ROUTER_SIZE) / (2 * (radius ** 2))));
        const matrix = matrixParam.clone().rotate(90);

        const path = [
            this.transformPathSegment('M', intersectionIndent, this.ROUTER_SIZE * -0.5, matrix),
            this.transformPathSegment('L', 0, this.ROUTER_SIZE * -0.5, matrix),
            this.transformPathSegment('L', 0, this.ROUTER_SIZE * 0.5, matrix),
            this.transformPathSegment('L', intersectionIndent, this.ROUTER_SIZE * 0.5, matrix),
        ];

        let rest = (2 * Math.PI) - radians;
        const step = rest / Math.floor(720 / this.DRILL_SIZE);

        while (rest > 0) {
            path.push(this.transformPathSegment(
                'L',
                (radius * Math.cos(rest + Math.PI)) + this.INDENT,
                radius * Math.sin(rest + Math.PI),
                matrix,
            ));
            rest -= step;
        }

        return [path];
    },

    /**
     * Calculates and stores margin path
     * @param {Matrix} matrix
     * @return {Array}
     */
    createMarginPath(matrix) {
        const drillRadius = this.DRILL_SIZE / 2;
        const horizontal = this.SIDE_MARGIN;
        const vertical = this.INDENT + this.TOP_MARGIN + drillRadius;

        const path = [
            this.transformPathSegment('M', -horizontal, 0, matrix),
            this.transformPathSegment('L', horizontal, 0, matrix),
            this.transformPathSegment('L', horizontal, vertical, matrix),
            this.transformPathSegment('L', -horizontal, vertical, matrix),
        ];

        return [path];
    },

    /**
     * transforms path segment
     * @param {string} command
     * @param {number} x
     * @param {number} y
     * @param {Matrix} matrix
     * @return {Array}
     */
    transformPathSegment(command, x, y, matrix) {
        return [command, matrix.x(x, y), matrix.y(x, y)];
    },

    /**
     * Execute changes on item 'change' event
     * @param {CutangleItem} item
     */
    onItemChange(item) {
        if (item.hasChangedProperty('line')) {
            this.set('ext', this.calculateConnectorSegments());
            this.set('orig', this.get('ext'));
            this.set('safety', this.calculateMarginSegments());
            this.trigger('updatesegments', this);
        }
    },

    /**
     * Returns the transformation matrix for conector items
     *
     * This overloads the default behavior, becuase connectors can't be
     * positioned by hand but have an implicit position by its line.
     *
     * @return {Matrix} Transformation Matrix
     */
    getMatrix() {
        const matrix = new Matrix();
        const { x, y } = this.get('position');

        matrix.translate(x, y);

        return matrix;
    },

    /**
     * Return safety margin for this item
     *
     * @return {number}
     */
    getSafetyMargin() {
        // signal maximum of possible margins to collisionManager to find adequate leader in case of collision
        return 0;
    },

    /**
     * Translate object into json stringable types. This method will be used
     * by JSON.stringify() automatically.
     *
     * @return {Object}
     */
    toJSON(...args) {
        const attributes = Ofcs.Editor.Item.AbstractItem.prototype.toJSON.apply(this, args);
        const segment = this.getTargetSegment();

        return {
            type: attributes.type,
            connectorOffsets: this.get('offsets'),
            line: {
                startPoint: {
                    x: attributes.line.startPoint.x.toFixed(5),
                    y: attributes.line.startPoint.y.toFixed(5),
                },
                endPoint: {
                    x: attributes.line.endPoint.x.toFixed(5),
                    y: attributes.line.endPoint.y.toFixed(5),
                },
            },
            segment: {
                startPoint: {
                    x: segment.startPoint.x.toFixed(5),
                    y: segment.startPoint.y.toFixed(5),
                },
                endPoint: {
                    x: segment.endPoint.x.toFixed(5),
                    y: segment.endPoint.y.toFixed(5),
                },
            },
            billable: attributes.billable,
        };
    },

    /**
     * Initializes item handles
     *
     * @param {Ofcs.Editor.Svg.Canvas} canvas - svg canvas instance
     * @return {ResizeCutangleHandle}
     */
    initializeHandles(canvas) {
        return new ResizeConnectorHandle(this, canvas);
    },

    /**
     * Searches tabletop's outline segments and
     * returns adjacent segment
     *
     * @param {Line} [line]
     * @return {*|Line}
     */
    getTargetSegment(line = this.get('line')) {
        const outlines = this.getParentItem().getOutlineSegments();
        const angle = Math.round(line.getAngle()) % 360;

        let j = 0;
        let distance;
        let index;

        while (outlines[j]) {
            const segmentAngle = Math.round(outlines[j].getAngle()) % 360;
            const angleDiff = Math.abs(angle - segmentAngle);
            if (distance === undefined || distance > angleDiff) {
                index = j;
                distance = angleDiff;
            }
            j += 1;
        }

        return outlines[index];
    },
});
